import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmLabel?: React.ReactNode;
  cancelLabel?: React.ReactNode;
  /** Render the primary action with the danger variant. */
  destructive?: boolean;
  /** When set, the operator must type this exact phrase before submitting. */
  requireType?: string;
  loading?: boolean;
  onConfirm: () => void | Promise<void>;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  destructive,
  requireType,
  loading,
  onConfirm,
}: ConfirmDialogProps) {
  const { t } = useTranslation('common');
  const [typed, setTyped] = React.useState('');
  const inputId = React.useId();

  React.useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  const matchesType = !requireType || typed === requireType;
  const submitDisabled = !matchesType || loading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : (
            <DialogDescription className="sr-only">
              {t('confirm.fallbackDescription', {
                defaultValue: 'Confirm the action below.',
              })}
            </DialogDescription>
          )}
        </DialogHeader>
        {requireType ? (
          <div className="mb-4">
            <Label htmlFor={inputId}>
              {t('confirm.typeToContinue', { phrase: requireType, defaultValue: 'Type {phrase} to continue' })}
            </Label>
            <Input
              id={inputId}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              aria-invalid={!matchesType}
              dir="ltr"
            />
          </div>
        ) : null}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            {cancelLabel ?? t('actions.cancel')}
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            onClick={() => {
              if (!submitDisabled) void onConfirm();
            }}
            disabled={submitDisabled}
            {...(loading ? { loading: true } : {})}
          >
            {confirmLabel ?? t('actions.continue')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
